import React, { memo, useEffect, useRef, useState } from 'react';
import { NodeProps, Position } from 'reactflow';
import { CanvasElementState } from '@components/MainStage/canvasStates';
import { ChangeInitiativeStatus } from '@components/MainStage/CustomNode/ChangeInitiativeStatus';
import { KeyControlsImpact } from '@components/MainStage/CustomNode/KeyControlsImpact';
import { RiskScore } from '@components/MainStage/CustomNode/RiskScore';
import {
  getHoverState,
  useIsEndpointOfHoveredEdge,
  useIsNodeHovered,
  useIsPointerOnNode,
} from '@components/MainStage/hover.store';
import { useSearchNeedle } from '@components/MainStage/listSearch.store';
import { useIsNodeMultiSelected } from '@components/MainStage/multiSelection.store';
import { KeyControlsImpactColor } from '@constants/canvas/canvasTheme';
import { InitiativeProgressStatus } from '@constants/canvas/layers';
import { useNodesDataContext } from '@context/NodesDataContext';
import { Typography } from '@mui/material';
import { NodeDTOTypes } from '@store/services/nodes/types';
import { ImpactType } from '@store/services/projects/types';
import { themePalette } from '@theme/muiTheme';
import { getInitiativeNodeIcon, getNodeIcon } from '@utils/canvasIconHelpers';
import { renderHighlightedText } from '@utils/searchHighlight';
import { Tabs } from '@views/Insights/types';

import {
  BottomHandleWrapper,
  CustomNodeContent,
  CustomNodeStyled,
  CustomNodeToolbar,
  EvolutionMaturityBlock,
  EvolutionMaturityWrapper,
  IconWrapper,
  IncompleteLabel,
  MaturityBlock,
  MaturityWrapper,
  StyledHandle,
  StyledText,
  StyledTooltipLeftArrow,
  TextWrapper,
  TopHandleWrapper,
} from './CustomNode.styled';

/** How long the pointer has to rest on an entity before its full label shows. */
const TOOLTIP_DELAY = 1000;

export const CustomNode = memo(function CustomNode({
  id,
  data,
  selected,
}: NodeProps) {
  const {
    getActiveNode,
    getActiveEdge,
    isConnectable,
    isNodeDisabled,
    insightsTab,
  } = useNodesDataContext();

  const [isHovered, setIsHovered] = useState(false);
  const hoverDelay = useRef<ReturnType<typeof setTimeout>>();
  const isMultiSelected = useIsNodeMultiSelected(id);
  const isNodeHovered = useIsNodeHovered(id);
  const isPointerOnNode = useIsPointerOnNode(id);
  const isEndpointOfHoveredEdge = useIsEndpointOfHoveredEdge(id);
  const searchNeedle = useSearchNeedle();

  const activeNode = getActiveNode();
  const activeEdge = getActiveEdge();

  const targetSelectedWithEdge = id === activeEdge?.target;
  const sourceSelectedWithEdge = id === activeEdge?.source;
  const { type, is_complete: isComplete, label, meta_data } = data.dto;
  const {
    selectedWithEdge,
    selectedInInsight,
    selectedInMaturity,
    maturityBackground,
    keyControlImpact,
    disabled,
    state = CanvasElementState.Default,
  } = data.canvas;

  const isIncomplete = !isComplete;
  const isDisabled =
    state === CanvasElementState.Disabled || isNodeDisabled(data.canvas);
  const isFaded = state === CanvasElementState.Faded;
  const isDimmed = isDisabled || isFaded;
  const isNodeTargetHidden = type === NodeDTOTypes.StrategicPriority;

  const isConnectorVisible =
    !isDisabled &&
    (isNodeHovered ||
      isEndpointOfHoveredEdge ||
      Boolean(selectedWithEdge && activeEdge?.id) ||
      Boolean(selected));

  const nodeIcon =
    type === NodeDTOTypes.Initiatives
      ? getInitiativeNodeIcon({
          type: meta_data?.type,
          disabled: isDimmed,
        })
      : getNodeIcon({ type, disabled: isDimmed });

  const handleMouseEnter = () => {
    getHoverState().setHovered(id);
    clearTimeout(hoverDelay.current);
    hoverDelay.current = setTimeout(() => {
      setIsHovered(true);
    }, TOOLTIP_DELAY);
  };

  const handleMouseLeave = () => {
    getHoverState().unhover(id);
  };

  // A mouseleave is not guaranteed to arrive on this canvas (QSP-3504), so the
  // tooltip follows the store hover, which the pointer itself retires.
  useEffect(() => {
    if (isPointerOnNode) return;

    clearTimeout(hoverDelay.current);
    setIsHovered(false);
  }, [isPointerOnNode]);

  const isSelectedInInsight =
    insightsTab === Tabs.Dependency &&
    selectedInInsight &&
    !activeNode &&
    !selectedWithEdge &&
    !activeEdge?.id;

  const shouldBeSelectedInMaturity =
    selectedInMaturity &&
    meta_data?.current_maturity &&
    meta_data?.target_maturity &&
    isComplete;

  const isInitiativeStatusShown =
    type === NodeDTOTypes.Initiatives &&
    (meta_data?.progress_status === InitiativeProgressStatus.Completed ||
      meta_data?.progress_status === InitiativeProgressStatus.Cancelled);

  const isKeyControlImpactShown =
    selectedInInsight && keyControlImpact && !disabled;
  const keyControlImpactColor =
    KeyControlsImpactColor[keyControlImpact as ImpactType];

  // eslint-disable-next-line no-nested-ternary
  const evolutionStatues = data?.diff
    ? data.diff > 0
      ? 'positive'
      : 'negative'
    : undefined;

  return (
    <CustomNodeStyled
      $selected={selected}
      $hovered={isNodeHovered || isEndpointOfHoveredEdge}
      $multiSelected={isMultiSelected}
      $incomplete={isIncomplete}
      $selectedWithEdge={selectedWithEdge && activeEdge?.id}
      $selectedInInsight={isSelectedInInsight}
      $selectedInMaturity={shouldBeSelectedInMaturity}
      $maturityBackground={maturityBackground}
      $keyControlsImpactColor={keyControlImpactColor}
      $disabled={isDisabled}
      $state={state}
      $evolution={evolutionStatues}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {isIncomplete && (
        <IncompleteLabel $disabled={isDimmed}>
          <Typography
            variant="body2"
            color={isDimmed ? 'grey.600' : 'system.error'}
          >
            Incomplete
          </Typography>
        </IncompleteLabel>
      )}

      {shouldBeSelectedInMaturity && (
        <MaturityWrapper>
          <MaturityBlock
            $maturityBackground={maturityBackground}
            $isDisabled={disabled}
          >
            {meta_data?.current_maturity}
          </MaturityBlock>
          <MaturityBlock $isDisabled={disabled}>
            {meta_data?.target_maturity}
          </MaturityBlock>
        </MaturityWrapper>
      )}

      {evolutionStatues && (
        <EvolutionMaturityWrapper>
          <EvolutionMaturityBlock $evolution={evolutionStatues}>
            {data.diff.toString().length === 1
              ? data.diff.toString()
              : data.diff.toFixed(1)}
          </EvolutionMaturityBlock>
        </EvolutionMaturityWrapper>
      )}

      {type === NodeDTOTypes.EvaluatedRisk && <RiskScore data={data} />}
      {isInitiativeStatusShown && (
        <ChangeInitiativeStatus status={meta_data.progress_status} />
      )}
      {isKeyControlImpactShown && (
        <KeyControlsImpact impact={keyControlImpact} />
      )}
      <CustomNodeToolbar
        isVisible={label.length > 30 && isHovered}
        position={Position.Right}
      >
        <StyledTooltipLeftArrow />
        <Typography
          variant="body2"
          color="white"
          sx={{ wordBreak: 'break-word' }}
        >
          {label}
        </Typography>
      </CustomNodeToolbar>
      <CustomNodeContent>
        <IconWrapper>{nodeIcon}</IconWrapper>
        <TextWrapper>
          <StyledText variant="h5" color={isDimmed ? 'grey.600' : 'grey.1000'}>
            {renderHighlightedText(
              data?.dto?.label ?? '',
              searchNeedle,
              themePalette.yellow[800],
            )}
          </StyledText>
        </TextWrapper>
      </CustomNodeContent>
      {!isNodeTargetHidden && (
        <TopHandleWrapper $connectorVisible={isConnectorVisible}>
          <StyledHandle
            type="target"
            position={Position.Top}
            $selectedWithEdge={targetSelectedWithEdge}
            $connectorVisible={isConnectorVisible}
            isConnectable={isConnectable}
            $faded={isDimmed}
          />
        </TopHandleWrapper>
      )}
      <BottomHandleWrapper $connectorVisible={isConnectorVisible}>
        <StyledHandle
          type="source"
          position={Position.Bottom}
          $selectedWithEdge={sourceSelectedWithEdge}
          $connectorVisible={isConnectorVisible}
          isConnectable={isConnectable}
          $faded={isDimmed}
        />
      </BottomHandleWrapper>
    </CustomNodeStyled>
  );
});
